import { cn } from '@solinkcorp/solink-ui';
import { useCallback, useEffect, useRef } from 'react';
import {
  clampInstant,
  clockUses12Hour,
  clockWindowForDay,
  formatTimezone,
  hourOn12HourClock,
  hourOn24HourClock,
  readTimeOfDay,
  selectable12HourLabels,
  selectableHours24,
  selectableMeridiem,
  selectableMinutes,
  withTimeOfDay,
  type InstantBounds,
} from './datePickerTime';

const ITEM_HEIGHT = 36;
const VISIBLE_ITEMS = 11;
const WHEEL_HEIGHT = ITEM_HEIGHT * VISIBLE_ITEMS;
const EDGE_PADDING = ((VISIBLE_ITEMS - 1) / 2) * ITEM_HEIGHT;

/** Scrollable hour / minute / meridiem wheels. Replaces the calendar while a time is being chosen. */
export function TimeSheet({
  value,
  timeZone,
  bounds,
  labels,
  onChange,
}: {
  value: number;
  timeZone: string;
  /** Hours and minutes outside this window are omitted. `latest` caps the current clock. */
  bounds: InstantBounds;
  labels: { hour: string; minute: string; am: string; pm: string };
  onChange: (next: number) => void;
}) {
  const shown = clampInstant(value, bounds);
  const { hour, minute } = readTimeOfDay(shown, timeZone);
  const uses12Hour = clockUses12Hour();
  const hour12 = hourOn12HourClock(hour);
  const window = clockWindowForDay(shown, timeZone, bounds);
  const meridiem = selectableMeridiem(window);

  const setClock = (nextHour: number, nextMinute: number) => {
    if (nextHour === hour && nextMinute === minute) {
      return;
    }
    onChange(
      clampInstant(withTimeOfDay(shown, timeZone, { hour: nextHour, minute: nextMinute }), bounds),
    );
  };

  const allowedHours = uses12Hour
    ? selectable12HourLabels(window, hour12.isPm)
    : selectableHours24(window);
  const allowedMinutes = selectableMinutes(hour, window);
  const hourValues = allowedHours.length > 0 ? allowedHours : [uses12Hour ? hour12.hour : hour];
  const minuteValues = allowedMinutes.length > 0 ? allowedMinutes : [minute];
  const meridiemValues = [...(meridiem.am ? [labels.am] : []), ...(meridiem.pm ? [labels.pm] : [])];
  if (uses12Hour && meridiemValues.length === 0) {
    meridiemValues.push(hour12.isPm ? labels.pm : labels.am);
  }

  return (
    <div
      className="flex min-h-[21rem] flex-col items-center justify-center gap-4"
      data-testid="date-picker-time-sheet"
    >
      <div className="flex items-center gap-6">
        <WheelColumn
          ariaLabel={labels.hour}
          values={hourValues}
          selected={uses12Hour ? hour12.hour : hour}
          format={pad2}
          onSelect={(next) =>
            setClock(uses12Hour ? hourOn24HourClock(next, hour12.isPm) : next, minute)
          }
        />
        <span className="flex flex-col items-center justify-center gap-1" aria-hidden>
          <span className="size-1 rounded-full bg-foreground-secondary" />
          <span className="size-1 rounded-full bg-foreground-secondary" />
        </span>
        <WheelColumn
          ariaLabel={labels.minute}
          values={minuteValues}
          selected={minute}
          format={pad2}
          onSelect={(next) => setClock(hour, next)}
        />
        {uses12Hour && (
          <WheelColumn
            ariaLabel={`${labels.am} / ${labels.pm}`}
            values={meridiemValues}
            selected={hour12.isPm ? labels.pm : labels.am}
            format={(item) => item}
            onSelect={(next) =>
              setClock(hourOn24HourClock(hour12.hour, next === labels.pm), minute)
            }
          />
        )}
      </div>
      <span className="text-foreground-secondary body-sm" data-testid="date-picker-timezone">
        {formatTimezone(value, timeZone)}
      </span>
    </div>
  );
}

function WheelColumn<T extends number | string>({
  ariaLabel,
  values,
  selected,
  format,
  onSelect,
}: {
  ariaLabel: string;
  values: readonly T[];
  selected: T;
  format: (value: T) => string;
  onSelect: (value: T) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const selectedIndex = Math.max(0, values.indexOf(selected));
  const programmaticRaf = useRef(0);
  const scrollEndTimer = useRef<ReturnType<typeof setTimeout>>();
  const isProgrammatic = useRef(false);

  const cancelPendingScroll = () => {
    cancelAnimationFrame(programmaticRaf.current);
    clearTimeout(scrollEndTimer.current);
  };

  const scrollToIndex = useCallback((index: number) => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    isProgrammatic.current = true;
    el.scrollTo({ top: index * ITEM_HEIGHT, behavior: 'auto' });
    cancelAnimationFrame(programmaticRaf.current);
    programmaticRaf.current = requestAnimationFrame(() => {
      isProgrammatic.current = false;
    });
  }, []);

  useEffect(() => {
    scrollToIndex(selectedIndex);
  }, [selectedIndex, scrollToIndex]);

  useEffect(() => {
    return () => {
      cancelAnimationFrame(programmaticRaf.current);
      clearTimeout(scrollEndTimer.current);
    };
  }, []);

  const indexFromScrollTop = (scrollTop: number) =>
    Math.max(0, Math.min(values.length - 1, Math.round(scrollTop / ITEM_HEIGHT)));

  const commitFromScroll = () => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const index = indexFromScrollTop(el.scrollTop);
    const next = values[index];
    const alreadySnapped = Math.abs(el.scrollTop - index * ITEM_HEIGHT) < 1;
    if (next === selected && alreadySnapped) {
      return;
    }
    scrollToIndex(index);
    if (next !== undefined && next !== selected) {
      onSelect(next);
    }
  };

  const handleScroll = () => {
    if (isProgrammatic.current) {
      return;
    }
    // Select only after the wheel stops. Selecting mid-flick scrolls back to that value and kills momentum.
    clearTimeout(scrollEndTimer.current);
    scrollEndTimer.current = setTimeout(commitFromScroll, 100);
  };

  return (
    <div
      className="relative w-[4.5rem]"
      style={{ height: WHEEL_HEIGHT }}
      role="listbox"
      aria-label={ariaLabel}
      aria-activedescendant={`${ariaLabel}-${String(selected)}`}
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-1/2 -z-0 -translate-y-1/2 rounded-md bg-primary"
        style={{ height: ITEM_HEIGHT }}
        aria-hidden
      />

      <div
        ref={scrollRef}
        className="relative z-[1] h-full snap-y snap-mandatory overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        onScroll={handleScroll}
      >
        <div style={{ height: EDGE_PADDING }} aria-hidden />
        {values.map((item) => {
          const isSelected = item === selected;
          return (
            <button
              key={String(item)}
              id={`${ariaLabel}-${String(item)}`}
              type="button"
              role="option"
              aria-selected={isSelected}
              className={cn(
                'flex w-full shrink-0 snap-center items-center justify-center p-0 tabular-nums body-md',
                isSelected ? 'font-medium text-primary-foreground' : 'text-foreground-secondary',
              )}
              style={{ height: ITEM_HEIGHT }}
              onClick={() => {
                cancelPendingScroll();
                scrollToIndex(values.indexOf(item));
                if (item !== selected) {
                  onSelect(item);
                }
              }}
            >
              {format(item)}
            </button>
          );
        })}
        <div style={{ height: EDGE_PADDING }} aria-hidden />
      </div>
    </div>
  );
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}
